/**
 * Hook customizado para filtros de busca
 */

import { useState, useCallback, useMemo } from 'react';
import { FormatUtil } from '@utils';

interface UseFilterReturn<T> {
  searchTerm: string;
  filteredData: T[];
  setSearchTerm: (term: string) => void;
  clearSearch: () => void;
  hasSearch: boolean;
}

export const useFilter = <T extends Record<string, any>>(
  data: T[],
  searchFields: (keyof T)[]
): UseFilterReturn<T> => {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredData = useMemo(() => {
    if (!searchTerm.trim()) {
      return data;
    }

    const normalizedTerm = FormatUtil.normalizeForSearch(searchTerm);

    return data.filter((item) => {
      return searchFields.some((field) => {
        const value = item[field];
        
        if (typeof value === 'string') {
          const normalizedValue = FormatUtil.normalizeForSearch(value);
          return normalizedValue.includes(normalizedTerm);
        }

        if (typeof value === 'number') {
          return value.toString().includes(normalizedTerm);
        }

        return false;
      });
    });
  }, [data, searchTerm, searchFields]);

  const clearSearch = useCallback(() => {
    setSearchTerm('');
  }, []);

  const hasSearch = searchTerm.trim().length > 0;

  return {
    searchTerm,
    filteredData,
    setSearchTerm,
    clearSearch,
    hasSearch,
  };
};